import { IonCol, IonContent, IonGrid, IonIcon, IonInput, IonPage, IonRow } from '@ionic/react';
import { searchSharp, closeCircleSharp } from 'ionicons/icons';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ListGrid from '../../components/ListGrid/ListGrid';
import OptionModal from '../../components/Modal/OptionModal';
import { Folder } from '../../models/Folder';
import DAService from '../../services/DAService.service';
import LocalStorage from '../../utils/LocalStorage';
import './Search.scss';

function Search() {
  const [searchResult, setSearchResult] = useState<any[] | null>();
  const [searchInput, setSearchInput] = useState('');
  const [inputTouched, setInputTouched] = useState(false);
  const [noResult, setNoResult] = useState(false);
  const [topicId, selectTopicId] = useState<string>('');
  const [option, setOption] = useState(null);
  const [topicIndex, setTopicIndex] = useState<number>(0);
  const [isEnterPressed, setIsEnterPressed] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef<any>(null);
  let trueIndex = 0;

  useEffect(() => {
    // reset input
    setSearchInput('');
    // reset results
    setSearchResult([]);
  }, [location]);

  useEffect(() => {
    DAService.getFolders().map((folder: Folder) => (
      folder.folderItems?.map((topic) => {
        trueIndex += 1;
        if (topicId === topic.itemId) {
          setTopicIndex(trueIndex);
          LocalStorage.set('topicIndex', topicIndex);
        }
        return true;
      })
    ));
  }, [topicId, topicIndex, option]);

  const getSearchResult = () => setSearchResult(DAService.search(searchInput));

  useEffect(() => {
    if (inputTouched && searchInput.length === 0 && searchInput.trim().length === 0) {
      // clear search results if input has been touched after mount and is cleared
      setSearchResult([]);
      setNoResult(false);
      setInputTouched(false);
      setIsEnterPressed(false);
    }

    const keyDownHandler = (event: any) => {
      if (event.key === 'Enter') {
        console.log('user press: ', event.key);
        event.preventDefault();
        setIsEnterPressed(true);

        if (searchInput.trim().length > 0) {
          // fetch result
          getSearchResult();
        }
      }
    };

    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [searchInput, option, isEnterPressed]);

  useEffect(() => {
    if (searchResult?.length === 0
      && inputTouched
      && isEnterPressed
      && searchInput.trim().length > 0) {
      setNoResult(true);
    }
    if (searchResult?.length && inputTouched) {
      setNoResult(false);
    }
  });

  const handleSearch = (e: CustomEvent): void => {
    DAService.search(e.detail?.value);
    setSearchInput(e.detail?.value);
    setInputTouched(true);
  };

  const navigateToTopicPage = (itemId: string) => () => {
    const topicPath: any[] = DAService.getPath(DAService.getData(), itemId, true) || [];
    const parentId = topicPath[topicPath.length - 2].itemId;
    selectTopicId(itemId);

    history.push(`/topics/${parentId}/${itemId}`);
  };

  const navigateToGoalsPage = (itemId: string) => () => {
    history.push(`/goals/${itemId}`);
  };

  const navigateToDecisionPage = (itemId: string) => () => {
    const decisionPath: any[] = DAService.getPath(DAService.getData(), itemId, true) || [];
    const parentId = decisionPath[decisionPath.length - 2].itemId;
    history.push({
      pathname: `/goals/${parentId}`,
      state: {
        itemId,
      },
    });
  };

  let textToShow = ' ';

  if (inputTouched && (searchResult && searchResult.length > 0)) {
    textToShow = `Found ${searchResult?.length} result${searchResult.length > 1 ? 's' : ''}...`;
  } else if (noResult && inputTouched) {
    textToShow = 'No result found...';
  }

  useEffect(() => {
    setTimeout(() => {
      inputRef.current.setFocus();
    }, 100);
  });

  return (
    <IonPage>
      <IonContent>
        <IonGrid className="search-input-container" style={{ padding: '0px 16px' }}>
          <OptionModal option={option} setOption={setOption} />
          <IonRow style={{ textAlign: 'center' }}>
            <IonCol size="11">
              <IonInput
                className="search-input-style"
                placeholder="Techniques, Roles, Skills and More"
                onIonChange={handleSearch}
                value={searchInput}
                ref={inputRef}
              >
                <IonIcon
                  icon={searchSharp}
                  className="search-icon-style"
                />
              </IonInput>
            </IonCol>

            <IonCol size="1" className="navigation-container" style={{ justifyContent: 'end' }}>
              <Link
                style={{ textDecoration: 'none', color: '#222222' }}
                to="/"
              >
                <IonIcon
                  icon={closeCircleSharp}
                  className="search-close"
                />
              </Link>
            </IonCol>
          </IonRow>
          <div className="result-text">
            {textToShow}
          </div>
          <div style={{ padding: '8px' }}>
            {
              searchResult?.map((result, index) => {
                // eslint-disable-next-line
                let Wrapper = ({ children }: { children: ReactNode }) => <>{children}</>;
                switch (result.type) {
                  case 'TOPIC':
                    // eslint-disable-next-line
                    Wrapper = ({ children }: { children: ReactNode }) => (
                      <button
                        type="button"
                        onClick={navigateToTopicPage(result.itemId)}
                        style={{ background: 'transparent', padding: '0px', textAlign: 'inherit', width: '100%' }}
                      >
                        {children}
                      </button>
                    );
                    break;
                  case 'OPTION':
                    // eslint-disable-next-line
                    Wrapper = ({ children }: { children: ReactNode }) => (
                      <button
                        style={{ background: 'transparent', padding: '0px', textAlign: 'inherit', width: '100%' }}
                        type="button"
                        onClick={(() => setOption(result))}
                      >
                        {children}
                      </button>
                    );
                    break;
                  case 'DECISION':
                    // eslint-disable-next-line
                    Wrapper = ({ children }: { children: ReactNode }) => (
                      <button
                        type="button"
                        onClick={navigateToDecisionPage(result.itemId)}
                        style={{ background: 'transparent', padding: '0px', textAlign: 'inherit', width: '100%' }}
                      >
                        {children}
                      </button>
                    );
                    break;
                  case 'GOAL':
                  case 'PROCESSGOAL':
                    // eslint-disable-next-line
                    Wrapper = ({ children }: { children: ReactNode }) => (
                      <button
                        type="button"
                        onClick={navigateToGoalsPage(result.itemId)}
                        style={{ background: 'transparent', padding: '0px', textAlign: 'inherit', width: '100%' }}
                      >
                        {children}
                      </button>
                    );
                    break;
                  default:
                    break;
                }

                return (
                  <Wrapper
                    key={`searchResult-${result.itemId}`}
                  >
                    <ListGrid
                      item={result}
                      arrayLength={searchResult?.length}
                      index={index}
                      itemType="default"
                    />
                  </Wrapper>
                );
              })
            }
          </div>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
}

export default Search;
